import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { useUtils } from "@/app/hooks/useUtils";
import { BsCurrencyRupee } from "react-icons/bs";
import { useLoader } from "@/app/hooks/useLoader";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import OMCalendarComponent from "@/app/components/ui/Calendar/OMCalendar";
import NoItemFoundComponent from "@/app/components/ui/NoItemFoundComponent";
import { IInventoryState } from "@/app_redux/reducers/slices/auth/inventory_slice";
import { useOrgCitiesListAPI } from "@/app/hooks/api_hooks/org/useOrgCitiesListAPI";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useStaffClinicListAPI } from "@/app/hooks/api_hooks/clinic/useStaffClinicListAPI";
import { IFinanceSummay, usePaymentFinanceSummayAPI } from "@/app/hooks/api_hooks/payment/usePaymentFinanceSummayAPI";

export interface InvoiceFilter {
  clinic: any;
  cities: string;
}

interface IFinanceSummaryRow {
  date?: string;
  payment_mode?: string;
  successful_transactions?: number;
  failed_transactions?: number;
  amount_collected?: number;
  amount_refunded?: number;
  amount_failed?: number;
}

const FinanceSummaryComponent = () => {
  const { veticDateFormat } = useUtils();
  const { NO_CART } = useImageConstants();
  const { clinics, cities } = useRouteInfo("INVENTORY")?.routeState?.state as IInventoryState;
  const { mappedClinicId, mappedCityId } = useAppSelector((state) => state.root.UIStates);
  const isTransactionLoading = useLoader("isFinanceSummaryLoading");
  const today = new Date();
  const [selectionDate, setSelectionDate] = useState({
    startDate: today,
    endDate: today,
    key: "selectionDate",
  });
  const startDate = new Date(selectionDate.startDate);
  const endDate = new Date(selectionDate.endDate);
  const startYear = startDate.getFullYear();
  const startMonth = (startDate.getMonth() + 1).toString().padStart(2, "0");
  const startDay = startDate.getDate().toString().padStart(2, "0");
  const endYear = endDate.getFullYear();
  const endMonth = (endDate.getMonth() + 1).toString().padStart(2, "0");
  const endDay = endDate.getDate().toString().padStart(2, "0");
  const formattedStartDate = `${startDay}-${startMonth}-${startYear}`;
  const formattedEndDate = `${endDay}-${endMonth}-${endYear}`;
  const [initial, setInitial] = useState(false);
  const [financeData, setFinanceData] = useState<IFinanceSummaryRow[]>([]);
  const clinicsOrgItem = useSelectMenuReducer(clinics, "display_name", "id");
  const cityOrgItem = useSelectMenuReducer(cities, "name", "id");
  const { execute: fetchFinanceSummary } = usePaymentFinanceSummayAPI();
  const fetchPWLClinics = useStaffClinicListAPI("INVENTORY");
  const fetchCities = useOrgCitiesListAPI("INVENTORY");

  const formHook = useForm<InvoiceFilter>({
    mode: "onChange",
    defaultValues: {
      cities: mappedCityId,
      clinic: mappedClinicId,
    },
  });

  const selectedCity = formHook.watch("cities");
  const selectedClinic = formHook.watch("clinic");

  useEffect(() => {
    fetchCities();
    const city = formHook?.getValues("cities");
    if (city?.length >= 0) {
      fetchPWLClinics({
        cityIds: [city],
        withProviders: false,
      });
    }
  }, [selectedCity]);

  useEffect(() => {
    if (selectedClinic && !initial) {
      const constructedBody: IFinanceSummay = {
        end_date: formattedEndDate,
        start_date: formattedStartDate,
        clinic_ids: [mappedClinicId],
      };
      fetchFinanceSummary(constructedBody).then(({ status, message }) => {
        if (status) {
          setFinanceData(Array.isArray(message) ? message : []);
        } else {
          setFinanceData([]);
        }
      });
      setInitial(true);
    }
  }, [selectedClinic, initial]);

  const onSubmit = (data: InvoiceFilter) => {
    const constructedBody: IFinanceSummay = {
      end_date: formattedEndDate,
      start_date: formattedStartDate,
      clinic_ids: [data?.clinic],
    };
    fetchFinanceSummary(constructedBody).then(({ status, message }) => {
      if (status) {
        setFinanceData(Array.isArray(message) ? message : []);
      } else {
        setFinanceData([]);
      }
    });
  };

  const formatAmount = (amount?: number) => Number(amount || 0).toFixed(2);
  const totals = financeData.reduce(
    (acc, item) => {
      acc.collected += Number(item.amount_collected || 0);
      acc.refunded += Number(item.amount_refunded || 0);
      acc.failed += Number(item.amount_failed || 0);
      return acc;
    },
    { collected: 0, refunded: 0, failed: 0 },
  );

  return (
    <div className="h-full">
      <div className="pb-0.5 rounded-lg bg-white px-2">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center border-b pb-2 gap-2">
          <p className="text-sm font-semibold mt-2">Financial Summary</p>
        </div>
        <form
          onSubmit={formHook.handleSubmit(onSubmit)}
          className="pt-2 flex flex-col xl:flex-row xl:items-center gap-3"
        >
          <div className="w-full xl:w-auto">
            <OMCalendarComponent
              selectionDate={selectionDate}
              setSelectionDate={setSelectionDate}
              startDate={formattedStartDate}
              endDate={formattedEndDate}
              handelClick={() => {
                // handelClick();
              }}
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full xl:w-auto">
            <div className="w-full sm:w-56">
              <SearchableSelectMenu
                errorMessages={[{ message: "Category is required", type: "required" }]}
                onSelectItem={(item) => {
                  if (item) {
                    formHook.setValue("cities", item.id);
                  }
                }}
                fieldError={formHook?.formState?.errors?.cities}
                register={formHook.register("cities", { required: true })}
                selectItems={cityOrgItem}
                placeholder="Select city"
                showTooltips={true}
                showTypedErrors={false}
                showDropdownIcon={true}
                defaultSelected={cityOrgItem?.filter((oc) => oc.id === selectedCity)[0]}
                listBoxClassName="w-full"
                className="text-gray-800 w-full"
                containerClassName="w-full"
              />
            </div>
            <div className="w-full sm:w-56">
              <SearchableSelectMenu
                errorMessages={[{ message: "Category is required", type: "required" }]}
                onSelectItem={(item) => {
                  if (item) {
                    formHook.setValue("clinic", item.id);
                  }
                }}
                fieldError={formHook?.formState?.errors?.clinic}
                register={formHook.register("clinic", { required: true })}
                selectItems={clinicsOrgItem}
                placeholder="Select clinic"
                showTooltips={true}
                showTypedErrors={false}
                showDropdownIcon={true}
                defaultSelected={clinicsOrgItem?.filter((oc) => oc.id === selectedClinic)[0]}
                listBoxClassName="w-full"
                className="text-gray-800 w-full"
                containerClassName="w-full"
              />
            </div>
          </div>
          <button
            className="bg-black text-xs text-white font-medium rounded-md px-3 h-9 w-full sm:w-32 shrink-0"
            type="submit"
          >
            Apply
          </button>
        </form>
      </div>
      {isTransactionLoading ? (
        <TableSkeleton />
      ) : financeData?.length > 0 ? (
        <div className="mt-2 scroll-inner !h-auto max-h-[520px] sm:max-h-[650px] flow-root relative rounded-xl bg-[#F9FAFB] overflow-auto">
          <div className="w-full">
            <div className="inline-block min-w-full align-middle">
              <table className="w-full min-w-[1040px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                <thead className="bg-[#F5F5F5] text-[#1F1F23] font-normal sticky top-0 z-20">
                  <tr>
                    {tableHeader.map((key, index) => (
                      <th
                        key={index}
                        scope="col"
                        className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black tracking-wider"
                      >
                        {key.item}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {financeData.map((entry, index) => {
                    const isFirst = index === 0;
                    const isLast = index === financeData.length - 1;
                    const isSingle = financeData.length === 1;
                    return (
                      <tr
                        key={index}
                        className={`
                          bg-white
                          ${isSingle ? "rounded-xl" : ""}
                          ${isFirst && !isSingle ? "rounded-t-xl" : ""}
                          ${isLast && !isSingle ? "rounded-b-xl" : ""}
                        `}
                      >
                        <td
                          className={`
                            whitespace-nowrap px-2 py-3 text-xs text-black
                            ${isSingle ? "rounded-tl-2xl rounded-bl-2xl" : ""}
                            ${isFirst && !isSingle ? "rounded-tl-2xl" : ""}
                            ${isLast && !isSingle ? "rounded-bl-2xl" : ""}
                          `}
                        >
                          {entry?.date ? veticDateFormat(entry?.date) : "NA"}
                        </td>
                        <td className="whitespace-nowrap px-2 py-3 text-xs text-black capitalize">
                          {entry?.payment_mode || "-"}
                        </td>
                        <td className="whitespace-nowrap px-2 py-3 text-xs text-black">
                          {entry?.successful_transactions ?? 0}
                        </td>
                        <td className="whitespace-nowrap px-2 py-3 text-xs text-black">
                          {entry?.failed_transactions ?? 0}
                        </td>
                        <td className="whitespace-nowrap px-2 py-3 text-xs text-[#079F60]">
                          <span className="inline-flex items-center">
                            <BsCurrencyRupee className="w-3 h-3" />
                            {formatAmount(entry?.amount_collected)}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-2 py-3 text-xs text-[#F59E0B]">
                          <span className="inline-flex items-center">
                            <BsCurrencyRupee className="w-3 h-3" />
                            {formatAmount(entry?.amount_refunded)}
                          </span>
                        </td>
                        <td
                          className={`
                            whitespace-nowrap px-2 py-3 text-xs text-[#FF0303]
                            ${isSingle ? "rounded-tr-2xl rounded-br-2xl" : ""}
                            ${isFirst && !isSingle ? "rounded-tr-2xl" : ""}
                            ${isLast && !isSingle ? "rounded-br-2xl" : ""}
                          `}
                        >
                          <span className="inline-flex items-center">
                            <BsCurrencyRupee className="w-3 h-3" />
                            {formatAmount(entry?.amount_failed)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="text-xs bg-[#F5F5F5] font-medium">
                    <td
                      colSpan={4}
                      className="sticky bottom-0 z-20 text-right px-6 py-3 bg-[#F5F5F5] border-t rounded-bl-2xl"
                    >
                      Total Value:
                    </td>
                    <td className="sticky bottom-0 z-20 px-2 py-3 bg-[#F5F5F5] border-t text-[#079F60]">
                      <span className="inline-flex items-center">
                        <BsCurrencyRupee className="w-3 h-3" />
                        {formatAmount(totals.collected)}
                      </span>
                    </td>
                    <td className="sticky bottom-0 z-20 px-2 py-3 bg-[#F5F5F5] border-t text-[#F59E0B]">
                      <span className="inline-flex items-center">
                        <BsCurrencyRupee className="w-3 h-3" />
                        {formatAmount(totals.refunded)}
                      </span>
                    </td>
                    <td className="sticky bottom-0 z-20 px-2 py-3 bg-[#F5F5F5] border-t rounded-br-2xl text-[#FF0303]">
                      <span className="inline-flex items-center">
                        <BsCurrencyRupee className="w-3 h-3" />
                        {formatAmount(totals.failed)}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        financeData?.length === 0 && (
          <div className="bg-white h-[400px] mt-3">
            <NoItemFoundComponent
              message=""
              description="No Financial Summary Found"
              imageURL={NO_CART}
              InputClassName="my-0 h-full"
            />
          </div>
        )
      )}
    </div>
  );
};

export default FinanceSummaryComponent;

const tableHeader = [
  { item: "Date" },
  { item: "Payment Mode" },
  { item: "Transactions (Success)" },
  { item: "Transactions (Failed)" },
  { item: "Amount Collected" },
  { item: "Amount Refunded" },
  { item: "Failed Amount" },
];

const TableSkeleton = () => {
  return (
    <div className="mt-3 scroll-inner !h-auto max-h-[520px] sm:max-h-[650px] flow-root relative rounded-xl bg-[#F9FAFB] animate-pulse">
      <div className="w-full overflow-x-auto">
        <div className="inline-block min-w-full align-middle">
          <table className="w-full min-w-[1040px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
            <thead className="bg-[#F5F5F5] text-[#1F1F23] font-normal sticky top-0 z-10">
              <tr>
                {[...Array(7)].map((_, index) => (
                  <th key={index} className="p-2 py-3 text-left text-xs font-medium text-gray-300 tracking-wider">
                    <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...Array(8)].map((_, rowIndex) => (
                <tr key={rowIndex} className="bg-white">
                  {[...Array(7)].map((_, colIndex) => (
                    <td key={colIndex} className="px-2 py-3 whitespace-nowrap text-xs text-gray-300">
                      <div className="h-3 bg-gray-200 rounded w-full"></div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
